import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, CalendarDays, Check, Clock, ExternalLink, Globe, Mail, MapPin, Users } from 'lucide-react'
import type { PublicCoursePage as CoursePageData } from '@/api/public'
import { formatPriceLabel } from '@/lib/pricing'
import { AppLink, CoursePicture, PicturePlaceholder } from './PublicCourseParts'

function TickList({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) return null
  return (
    <section>
      <h2 className="mb-2 text-lg font-semibold text-slate-900">{title}</h2>
      <ul className="space-y-1.5">
        {items.map(item => (
          <li key={item} className="flex items-start gap-2 text-slate-700">
            <Check size={16} className="mt-1 shrink-0 text-[var(--brand-accent)]" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * Everything about one course: pictures, the content the customer wrote, and its upcoming dates with a way to book.
 * Used by the hosted course page and, with <c>embed</c>, inside a customer's iframe.
 */
export default function CourseDetail({ data, slug, embed, fmtMoney, formatDate, formatTime }: {
  data: CoursePageData
  slug: string
  embed?: boolean
  fmtMoney: (n: number) => string
  formatDate: (value: string, opts?: Intl.DateTimeFormatOptions) => string
  formatTime: (value: string) => string
}) {
  const [picked, setPicked] = useState(0)
  const image = data.images[Math.min(picked, data.images.length - 1)]
  const price = formatPriceLabel(data.pricingModel, data.price, data.flatFeeAmount, data.flatFeeCapacity, fmtMoney)
  const hasDates = data.sessions.length > 0

  return (
    <div className="space-y-8">
        {!embed && (
          <Link to={`/courses/${slug}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--brand-accent)] hover:opacity-80">
            <ArrowLeft size={14} /> All courses
          </Link>
        )}

        <div className="grid gap-8 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          {/* Pictures */}
          <div className="space-y-3">
            {image
              ? <CoursePicture image={image} eager sizes="(min-width: 1024px) 600px, 100vw" className="aspect-[3/2] w-full rounded-xl border border-slate-200" />
              : <PicturePlaceholder className="aspect-[3/2] w-full rounded-xl border border-slate-200" />}
            {data.images.length > 1 && (
              <ul className="flex flex-wrap gap-2" aria-label="More pictures">
                {data.images.map((img, i) => (
                  <li key={img.id}>
                    <button
                      type="button"
                      onClick={() => setPicked(i)}
                      aria-label={`Show picture ${i + 1}: ${img.altText}`}
                      aria-current={i === picked}
                      className={`block overflow-hidden rounded-lg border-2 cursor-pointer ${i === picked ? 'border-[var(--brand-accent)]' : 'border-transparent opacity-80 hover:opacity-100'}`}
                    >
                      <CoursePicture image={img} sizes="96px" className="h-16 w-24" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Title and facts */}
          <div className="space-y-4">
            {data.category && (
              <AppLink to={`/courses/${slug}`} embed={embed} className="text-xs font-semibold uppercase tracking-wide text-[var(--brand-accent)] hover:opacity-80">{data.category.name}</AppLink>
            )}
            <h1 className="text-3xl font-bold text-slate-900">{data.name}</h1>
            <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-slate-600">
              {data.duration && <span className="flex items-center gap-1.5"><Clock size={16} className="text-slate-400" />{data.duration}</span>}
              {data.code && <span className="text-sm text-slate-400">{data.code}</span>}
              {data.isThirdPartyCertified && data.thirdPartyProviderName && <span className="text-sm">Certified by {data.thirdPartyProviderName}</span>}
            </div>
            {price && <p className="text-2xl font-bold text-slate-900">{hasDates ? price : `From ${price}`}</p>}
            {hasDates && (
              <a href="#dates" className="inline-block rounded-lg bg-[var(--brand-accent)] px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90">See dates and book</a>
            )}
            {data.whoItsFor && (
              <div>
                <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-slate-500">Who it's for</h2>
                <p className="text-slate-700">{data.whoItsFor}</p>
              </div>
            )}
          </div>
        </div>

        {/* Overview. The HTML is made and cleaned on the server from the customer's markdown. */}
        {data.overviewHtml && (
          <section aria-labelledby="course-about" className="max-w-3xl">
            <h2 id="course-about" className="mb-2 text-lg font-semibold text-slate-900">About this course</h2>
            <div
              className="text-slate-700 leading-relaxed [&_a]:text-[var(--brand-accent)] [&_a]:underline [&_h1]:mt-4 [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:mt-4 [&_h2]:text-xl [&_h2]:font-semibold [&_h3]:mt-3 [&_h3]:font-semibold [&_li]:ml-5 [&_ol]:list-decimal [&_p]:mb-3 [&_ul]:list-disc"
              dangerouslySetInnerHTML={{ __html: data.overviewHtml }}
            />
          </section>
        )}

        <div className="grid max-w-3xl gap-6">
          <TickList title="What you'll learn" items={data.whatYoullLearn} />
          <TickList title="Requirements" items={data.requirements} />
          <TickList title="What's included" items={data.whatsIncluded} />
        </div>

        {/* Dates */}
        <section id="dates" aria-label="Upcoming dates" className="space-y-3 scroll-mt-6">
          <h2 className="text-xl font-semibold text-slate-900">Upcoming dates</h2>
          {hasDates ? (
            <ul className="space-y-3">
              {data.sessions.map(session => {
                const sessionPrice = formatPriceLabel(session.pricingModel, session.price, session.flatFeeAmount, session.flatFeeCapacity, fmtMoney)
                const full = session.availablePlaces === 0
                return (
                  <li key={session.id}>
                    <AppLink
                      to={`/sessions/${slug}/${session.id}`}
                      embed={embed}
                      className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-4 transition-all hover:border-[var(--brand-accent)] hover:shadow-sm"
                    >
                      <div className="min-w-0 space-y-1 text-sm text-slate-500">
                        <p className="flex items-center gap-1.5 font-semibold text-slate-900">
                          <CalendarDays size={15} className="text-slate-400" />
                          {formatDate(session.startsAt, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1">
                          <span className="flex items-center gap-1.5"><Clock size={14} className="text-slate-400" />{formatTime(session.startsAt)}{session.endsAt && ` – ${formatTime(session.endsAt)}`}</span>
                          {session.deliveryMethod === 'Online'
                            ? <span className="flex items-center gap-1.5"><Globe size={14} className="text-slate-400" />Online</span>
                            : session.venue && <span className="flex items-center gap-1.5"><MapPin size={14} className="text-slate-400" />{session.venue.name}{session.venue.city ? `, ${session.venue.city}` : ''}</span>}
                          <span className="flex items-center gap-1.5"><Users size={14} className="text-slate-400" />{full ? 'Fully booked' : `${session.availablePlaces} place${session.availablePlaces === 1 ? '' : 's'} left`}</span>
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="font-bold text-slate-900">{sessionPrice ?? 'Free'}</p>
                        <p className="text-sm font-medium text-[var(--brand-accent)]">{full ? 'View' : 'Book →'}</p>
                      </div>
                    </AppLink>
                  </li>
                )
              })}
            </ul>
          ) : (
            <div className="space-y-2 rounded-xl border border-dashed border-slate-300 bg-white p-6">
              <p className="font-medium text-slate-800">No dates yet</p>
              <p className="text-sm text-slate-600">Get in touch to ask about the next course, or to arrange one for your team.</p>
              <div className="flex flex-wrap gap-4 pt-1 text-sm font-medium">
                {data.contactEmail && (
                  <a href={`mailto:${data.contactEmail}?subject=${encodeURIComponent(`Enquiry: ${data.name}`)}`} className="inline-flex items-center gap-1.5 text-[var(--brand-accent)] hover:opacity-80">
                    <Mail size={15} /> {data.contactEmail}
                  </a>
                )}
                {data.website && (
                  <a href={data.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[var(--brand-accent)] hover:opacity-80">
                    <ExternalLink size={15} /> Visit our website
                  </a>
                )}
                {!data.contactEmail && !data.website && <span className="text-slate-500">Contact {data.tenantName} to ask about dates.</span>}
              </div>
            </div>
          )}
        </section>

        {data.websiteUrl && (
          <p className="text-sm text-slate-500">
            More about this course on{' '}
            <a href={data.websiteUrl} target={embed ? '_top' : '_blank'} rel="noopener noreferrer" className="font-medium text-[var(--brand-accent)] hover:opacity-80">our website</a>.
          </p>
        )}
    </div>
  )
}
